import { useState, useEffect } from 'react';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import PasswordChangeModal from './PasswordChangeModal';
import MfaSetupModal from './MfaSetupModal';
import MfaDisableModal from './MfaDisableModal';
import SmsMfaSetupModal from './SmsMfaSetupModal';
import SmsMfaDisableModal from './SmsMfaDisableModal';
import PasskeyList from './PasskeyList';
import PasskeySetupModal from './PasskeySetupModal';
import DeviceList from './DeviceList';
import SecurityAlertList from './SecurityAlertList';
import { settingsApi, type SecurityStatus, type SessionPreferences, type SessionTimeoutOption } from '@/services/settings';
import SessionTimeoutSelect from './SessionTimeoutSelect';
import { toast } from '@/components/ui/Toaster';
import { ApiClientError } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { isWebAuthnSupported } from '@/utils/webauthn';

/**
 * Security settings component
 * Manages password, MFA, and passkey settings
 */
export default function SecuritySettings() {
  const { logout } = useAuthStore();
  const [securityStatus, setSecurityStatus] = useState<SecurityStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Session preferences state
  const [sessionPrefs, setSessionPrefs] = useState<SessionPreferences | null>(null);
  const [isLoadingPrefs, setIsLoadingPrefs] = useState(true);
  const [isUpdatingTimeout, setIsUpdatingTimeout] = useState(false);

  // Modal states
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isMfaSetupModalOpen, setIsMfaSetupModalOpen] = useState(false);
  const [isMfaDisableModalOpen, setIsMfaDisableModalOpen] = useState(false);
  const [isSmsMfaSetupModalOpen, setIsSmsMfaSetupModalOpen] = useState(false);
  const [isSmsMfaDisableModalOpen, setIsSmsMfaDisableModalOpen] = useState(false);
  const [isPasskeySetupModalOpen, setIsPasskeySetupModalOpen] = useState(false);
  const [passkeyRefreshTrigger, setPasskeyRefreshTrigger] = useState(0);

  // Check WebAuthn support
  const webAuthnSupported = isWebAuthnSupported();

  // Fetch security status and session preferences
  useEffect(() => {
    fetchSecurityStatus();
    fetchSessionPreferences();
  }, []);

  const fetchSecurityStatus = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const status = await settingsApi.getSecurityStatus();
      setSecurityStatus(status);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to load security settings.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSessionPreferences = async () => {
    setIsLoadingPrefs(true);
    try {
      const prefs = await settingsApi.getSessionPreferences();
      setSessionPrefs(prefs);
    } catch (err) {
      // Session preferences are non-critical, don't show error
      // Log for debugging in development only
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.debug('Failed to load session preferences:', err instanceof Error ? err.message : 'Unknown error');
      }
    } finally {
      setIsLoadingPrefs(false);
    }
  };

  const handleTimeoutChange = async (newTimeout: SessionTimeoutOption) => {
    if (!sessionPrefs) return;

    // Optimistic update
    const previousTimeout = sessionPrefs.sessionTimeout;
    setSessionPrefs({ ...sessionPrefs, sessionTimeout: newTimeout });
    setIsUpdatingTimeout(true);

    try {
      const updated = await settingsApi.updateSessionPreferences({
        sessionTimeout: newTimeout,
      });
      setSessionPrefs(updated);
      toast.success('Session timeout updated');
    } catch (err) {
      // Rollback on error
      setSessionPrefs({ ...sessionPrefs, sessionTimeout: previousTimeout });
      if (err instanceof ApiClientError) {
        toast.error(err.detail || 'Failed to update session timeout');
      } else {
        toast.error('Failed to update session timeout');
      }
    } finally {
      setIsUpdatingTimeout(false);
    }
  };

  const handlePasswordChangeSuccess = () => {
    setSuccessMessage('Password changed successfully. You will be logged out of all other sessions.');
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleMfaSetupSuccess = () => {
    setSuccessMessage('Two-factor authentication is now enabled.');
    fetchSecurityStatus();
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleMfaDisableSuccess = () => {
    setSuccessMessage('Two-factor authentication has been disabled.');
    fetchSecurityStatus();
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleSmsMfaSetupSuccess = () => {
    setSuccessMessage('SMS backup has been enabled.');
    fetchSecurityStatus();
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleSmsMfaDisableSuccess = () => {
    setSuccessMessage('SMS backup has been disabled.');
    fetchSecurityStatus();
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handlePasskeySetupSuccess = () => {
    setSuccessMessage('Passkey has been registered successfully.');
    fetchSecurityStatus();
    setPasskeyRefreshTrigger((prev) => prev + 1);
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleLogoutAllSessions = async () => {
    if (!window.confirm('This will log you out of all devices including this one. Continue?')) {
      return;
    }

    try {
      await settingsApi.logoutAllSessions();
      logout();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to log out of all sessions.');
      }
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader
          title="Security"
          description="Manage your security settings and authentication methods"
        />

        {error && (
          <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert variant="success" className="mb-4" onDismiss={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        <div className="space-y-1 divide-y divide-gray-100">
          {/* Password */}
          <div className="flex items-center justify-between py-4">
            <div>
              <div className="font-medium text-gray-900">Password</div>
              <div className="text-sm text-gray-500">
                Last changed: {formatDate(securityStatus?.passwordLastChanged ?? null)}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsPasswordModalOpen(true)}
            >
              Change password
            </Button>
          </div>

          {/* Two-Factor Authentication */}
          <div className="flex items-center justify-between py-4">
            <div>
              <div className="font-medium text-gray-900">Two-factor authentication</div>
              <div className="text-sm text-gray-500">
                {securityStatus?.mfaEnabled
                  ? 'Enabled - Your account is protected with TOTP'
                  : 'Add an extra layer of security to your account'}
              </div>
            </div>
            {securityStatus?.mfaEnabled ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsMfaDisableModalOpen(true)}
              >
                Disable
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsMfaSetupModalOpen(true)}
              >
                Enable
              </Button>
            )}
          </div>

          {/* SMS Backup */}
          <div className="flex items-center justify-between py-4">
            <div>
              <div className="font-medium text-gray-900">SMS Backup</div>
              <div className="text-sm text-gray-500">
                {securityStatus?.smsEnabled && securityStatus?.phoneLastFour
                  ? `Enabled - Phone ending in ${securityStatus.phoneLastFour}`
                  : securityStatus?.mfaEnabled
                    ? 'Use SMS as a backup authentication method'
                    : 'Requires authenticator app to be enabled first'}
              </div>
            </div>
            {securityStatus?.smsEnabled ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsSmsMfaDisableModalOpen(true)}
              >
                Disable
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsSmsMfaSetupModalOpen(true)}
                disabled={!securityStatus?.mfaEnabled}
              >
                Set up
              </Button>
            )}
          </div>

          {/* Passkey */}
          {webAuthnSupported && (
            <div className="flex items-center justify-between py-4">
              <div>
                <div className="font-medium text-gray-900">Passkey</div>
                <div className="text-sm text-gray-500">
                  {securityStatus?.passkeyEnabled
                    ? `${securityStatus.passkeyCount} passkey(s) registered`
                    : 'Sign in with biometric authentication'}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsPasskeySetupModalOpen(true)}
              >
                {securityStatus?.passkeyEnabled ? 'Add another' : 'Set up'}
              </Button>
            </div>
          )}

          {/* Active Sessions */}
          <div className="flex items-center justify-between py-4">
            <div>
              <div className="font-medium text-gray-900">Active sessions</div>
              <div className="text-sm text-gray-500">
                {securityStatus?.activeSessions
                  ? `${securityStatus.activeSessions} active session(s)`
                  : 'Manage devices logged into your account'}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogoutAllSessions}
            >
              Log out all
            </Button>
          </div>

          {/* Session Settings */}
          <div className="flex items-center justify-between py-4" aria-busy={isLoadingPrefs}>
            <div>
              <div className="font-medium text-gray-900">Auto-logout</div>
              <div className="text-sm text-gray-500">
                Automatically log out after inactivity.{' '}
                <span className="text-gray-400">
                  Adjust to balance security and convenience.
                </span>
              </div>
            </div>
            {isLoadingPrefs ? (
              <Spinner size="sm" />
            ) : (
              <SessionTimeoutSelect
                value={sessionPrefs?.sessionTimeout ?? '4hr'}
                onChange={handleTimeoutChange}
                disabled={isUpdatingTimeout}
              />
            )}
          </div>
        </div>
      </Card>

      {/* Modals */}
      <PasswordChangeModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onSuccess={handlePasswordChangeSuccess}
      />

      <MfaSetupModal
        isOpen={isMfaSetupModalOpen}
        onClose={() => setIsMfaSetupModalOpen(false)}
        onSuccess={handleMfaSetupSuccess}
      />

      <MfaDisableModal
        isOpen={isMfaDisableModalOpen}
        onClose={() => setIsMfaDisableModalOpen(false)}
        onSuccess={handleMfaDisableSuccess}
      />

      <SmsMfaSetupModal
        isOpen={isSmsMfaSetupModalOpen}
        onClose={() => setIsSmsMfaSetupModalOpen(false)}
        onSuccess={handleSmsMfaSetupSuccess}
        totpEnabled={securityStatus?.mfaEnabled ?? false}
      />

      <SmsMfaDisableModal
        isOpen={isSmsMfaDisableModalOpen}
        onClose={() => setIsSmsMfaDisableModalOpen(false)}
        onSuccess={handleSmsMfaDisableSuccess}
        phoneLastFour={securityStatus?.phoneLastFour ?? null}
      />

      <PasskeySetupModal
        isOpen={isPasskeySetupModalOpen}
        onClose={() => setIsPasskeySetupModalOpen(false)}
        onSuccess={handlePasskeySetupSuccess}
      />

      {/* Passkey Management */}
      {webAuthnSupported && (
        <PasskeyList
          className="mt-6"
          onAddPasskey={() => setIsPasskeySetupModalOpen(true)}
          refreshTrigger={passkeyRefreshTrigger}
        />
      )}

      {/* Device Management */}
      <DeviceList className="mt-6" />

      {/* Security Alerts */}
      <SecurityAlertList className="mt-6" />
    </>
  );
}
